import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, put } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import { RESOURCES_TAB_ENABLED } from '@/lib/use-resources-tab-enabled';

/**
 * Organization switches — "Clinic services" (PROD-2845, backend PROD-2840 /
 * Scrips.Organization #280). Per organization, all default OFF.
 *
 *   GET /api/v1/OrganizationSettings/{organizationId}/ClinicServices
 *     → { organizationId, sendsTestsToLabs, physiciansMayChangeLab, booksDevices }
 *     403 unless the token's orgId is {organizationId} (super admins: any).
 *     Before the DB script runs, all three read false.
 *
 *   PUT /api/v1/OrganizationSettings/{organizationId}/ClinicServices
 *     body { sendsTestsToLabs, physiciansMayChangeLab, booksDevices } (all required)
 *     200 → same shape as the GET. 400 missing field, 403 not allowed,
 *     404 no settings row, 503 script not run.
 *
 * ONE query, one key, shared by every consumer (the Settings page, the
 * devices gate, and PROD-2842's Lab vendors gate), so the switches are read
 * once per organization and a save updates every surface at once.
 */

export interface ClinicServicesSwitches {
  sendsTestsToLabs: boolean;
  physiciansMayChangeLab: boolean;
  booksDevices: boolean;
}

export interface ClinicServices extends ClinicServicesSwitches {
  organizationId: string;
  /**
   * Document Intake for this organization (Organization #282; set by SQL only, never by the PUT). Patient #1157
   * answers 404 `intake_disabled` on every intake route while it is off, so the screens hide intake too.
   */
  documentIntake: boolean;
}

export const clinicServicesQueryKey = (organizationId: string | null | undefined) =>
  ['settings', 'clinic-services', organizationId ?? null] as const;

export const clinicServicesUrl = (organizationId: string) =>
  `/api/v1/OrganizationSettings/${encodeURIComponent(organizationId)}/ClinicServices`;

/** All three off — the backend default, and what every gate assumes until the read lands. */
export const CLINIC_SERVICES_OFF: ClinicServicesSwitches = {
  sendsTestsToLabs: false,
  physiciansMayChangeLab: false,
  booksDevices: false,
};

/** Coerce the wire row: anything that is not literally `true` is off (fail closed). */
export function normaliseClinicServices(raw: Partial<ClinicServices> | null | undefined, organizationId: string): ClinicServices {
  return {
    organizationId: raw?.organizationId ?? organizationId,
    sendsTestsToLabs: raw?.sendsTestsToLabs === true,
    physiciansMayChangeLab: raw?.physiciansMayChangeLab === true,
    booksDevices: raw?.booksDevices === true,
    documentIntake: raw?.documentIntake === true,
  };
}

/** True for the 503 the PUT returns until the PROD-2840 script has run on this environment. */
export function isClinicServicesUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503;
}

/**
 * The physician lab-change switch only means something while the clinic sends
 * tests to labs: with labs off every order is Patient's choice of lab, so
 * there is nothing to change. Consumers read the EFFECTIVE value from here
 * rather than re-deriving the rule.
 */
export function effectivePhysiciansMayChangeLab(s: ClinicServicesSwitches): boolean {
  return s.sendsTestsToLabs && s.physiciansMayChangeLab;
}

/**
 * Devices show only when BOTH the build-time release switch
 * (`VITE_RESOURCES_TAB`) is on AND the organization books devices.
 */
export function devicesEnabled(buildFlag: boolean, booksDevices: boolean): boolean {
  return buildFlag && booksDevices;
}

/**
 * Read the current organization's clinic services.
 *
 * Returns the raw query plus `switches`: the three values with the rules
 * applied (`physiciansMayChangeLab` already AND-ed with `sendsTestsToLabs`).
 * While loading, or if the read fails, `switches` is all off.
 */
export function useClinicServices(organizationIdOverride?: string | null) {
  const authOrgId = useAuthStore((s) => s.user?.organizationId);
  const organizationId = organizationIdOverride ?? authOrgId ?? null;

  const query = useQuery({
    queryKey: clinicServicesQueryKey(organizationId),
    queryFn: async ({ signal }) => {
      if (!organizationId) throw new Error('useClinicServices called without organizationId');
      const raw = await get<Partial<ClinicServices>>(clinicServicesUrl(organizationId), { signal });
      return normaliseClinicServices(raw, organizationId);
    },
    enabled: !!organizationId,
    // Settings, not live data: read once and reuse across every screen.
    staleTime: 5 * 60 * 1000,
    retry: (count, error) =>
      !(error instanceof ApiError && [403, 404, 503].includes(error.status)) && count < 2,
  });

  const data = query.data;
  const switches: ClinicServicesSwitches = data
    ? {
        sendsTestsToLabs: data.sendsTestsToLabs,
        physiciansMayChangeLab: effectivePhysiciansMayChangeLab(data),
        booksDevices: data.booksDevices,
      }
    : CLINIC_SERVICES_OFF;

  return { ...query, organizationId, switches };
}

/**
 * Whether device surfaces show: Settings › Resources (devices), the
 * calendar's Devices view and device booking requests. Off while the read is
 * in flight, so a device control never flashes up for an organization that
 * doesn't book devices.
 */
export function useDevicesEnabled(
  /** The build flag; a parameter only so tests can drive both states. */
  buildFlag: boolean = RESOURCES_TAB_ENABLED,
): { enabled: boolean; isLoading: boolean } {
  const { switches, isLoading } = useClinicServices();
  if (!buildFlag) return { enabled: false, isLoading: false };
  return { enabled: devicesEnabled(buildFlag, switches.booksDevices), isLoading };
}

/**
 * Whether the intake entry points show for this organization: the chart Documents inbox and the lab-desk upload and
 * reading. Off while the read is in flight or failed, the same as the Patient gate (it fails closed). The `?x=intake` /
 * `?x=intake-desk` switches still apply on top. The review route stays on the switches alone (a deep link must not
 * fall through to the patients list while this read loads); with Document Intake off, Patient answers 404
 * `intake_disabled` and the review page says so.
 */
export function useDocumentIntakeEnabled(): { enabled: boolean; isLoading: boolean } {
  const { data, isLoading } = useClinicServices();
  return { enabled: data?.documentIntake === true, isLoading };
}

/** Save the three switches. On success the shared query is replaced with the server's row. */
export function useSaveClinicServices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, ...body }: ClinicServicesSwitches & { organizationId: string }) =>
      put<Partial<ClinicServices>>(clinicServicesUrl(organizationId), {
        sendsTestsToLabs: body.sendsTestsToLabs,
        physiciansMayChangeLab: body.physiciansMayChangeLab,
        booksDevices: body.booksDevices,
      }),
    onSuccess: (saved, vars) => {
      const next = normaliseClinicServices(saved ?? vars, vars.organizationId);
      queryClient.setQueryData(clinicServicesQueryKey(vars.organizationId), next);
    },
  });
}

/**
 * PROD-2856 (Scrips.AppointmentScheduling #2003): how many device bookings
 * the organization still has ahead.
 *
 *   GET /api/Appointment/ResourceList/FutureCount[?practiceId=]
 *     → { organizationId, practiceId, count, asOfUtc }
 *     Counts bookings with a Device participant that start after now and are
 *     not cancelled, entered in error or archived. The organization is the
 *     token's; 403 when the token has none.
 *
 * The switch is per organization, so the page asks for the whole
 * organization (no practice filter).
 */
export const futureDeviceBookingCountUrl = '/api/Appointment/ResourceList/FutureCount';

export interface FutureDeviceBookingCount {
  organizationId: string;
  practiceId: string | null;
  count: number;
  asOfUtc: string;
}

/**
 * Read the count at the moment the user turns devices off. FAIL-OPEN: any
 * error, or an unusable body, reads as 0, so a missing or failing endpoint
 * never stops the organization from saving its own setting. The warning is a
 * courtesy; the bookings stay booked either way.
 */
export async function fetchFutureDeviceBookingCount(): Promise<number> {
  try {
    const body = await get<Partial<FutureDeviceBookingCount>>(futureDeviceBookingCountUrl);
    const count = body?.count;
    return typeof count === 'number' && Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  } catch {
    return 0;
  }
}

export const futureDeviceBookingCountQueryKey = (organizationId: string | null | undefined) =>
  ['settings', 'clinic-services', 'future-device-bookings', organizationId ?? null] as const;

/**
 * Returns a function the page calls when "books devices" goes from on to off.
 * It always reads fresh (bookings change all day) through the shared
 * QueryClient, and resolves to the count, 0 on any failure.
 */
export function useFutureDeviceBookingCountCheck() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.user?.organizationId) ?? null;
  return () =>
    queryClient
      .fetchQuery({
        queryKey: futureDeviceBookingCountQueryKey(organizationId),
        queryFn: fetchFutureDeviceBookingCount,
        staleTime: 0,
        retry: false,
      })
      .catch(() => 0);
}
